import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { tasks } from "../../lib/api";
import type { Idea } from "../../lib/types";
import { Button } from "../shared/Button";
import { Modal } from "../shared/Modal";
import { ProposalFacts } from "./SpawnedProposalsCard";

/**
 * Every follow-up still waiting on the operator, across the project's tasks:
 * the ones that may become a task once accepted, and the parked ideas (no
 * evidence, or new scope) that never become tasks on their own.
 */
export function IdeasModal({ projectId, onClose }: { projectId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const ideas = useQuery({
    queryKey: ["ideas", projectId],
    queryFn: () => tasks.ideas(projectId),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["ideas"] });
    qc.invalidateQueries({ queryKey: ["kanban"] });
    qc.invalidateQueries({ queryKey: ["tasks"] });
  };
  const run = async (key: string, action: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(key);
    try {
      await action();
      refresh();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : "Could not update the proposal");
    } finally {
      setBusy(null);
    }
  };
  const rows = ideas.data ?? [];
  const waiting = rows.filter((i) => !i.proposal.parked);
  const parked = rows.filter((i) => i.proposal.parked);

  return (
    <Modal size="lg" title="Ideas and proposed follow-ups" onClose={onClose}>
      {ideas.isLoading && (
        <div className="py-8 text-center text-small text-text-tertiary">Loading…</div>
      )}
      {!ideas.isLoading && rows.length === 0 && (
        <div className="py-8 text-center text-small text-text-tertiary">
          Nothing is waiting for your OK.
        </div>
      )}
      {waiting.length > 0 && (
        <Section title={`Waiting for your OK (${waiting.length})`} ideas={waiting} busy={busy} run={run} />
      )}
      {parked.length > 0 && (
        <Section
          title={`Parked ideas (${parked.length})`}
          hint="No evidence yet, or new scope. They never become tasks unless you approve one."
          ideas={parked}
          busy={busy}
          run={run}
        />
      )}
    </Modal>
  );
}

function Section({
  title,
  hint,
  ideas,
  busy,
  run,
}: {
  title: string;
  hint?: string;
  ideas: Idea[];
  busy: string | null;
  run: (key: string, action: () => Promise<unknown>) => Promise<void>;
}) {
  return (
    <div className="mb-4">
      <div className="text-small font-medium text-text-primary">{title}</div>
      {hint && <div className="text-caption text-text-tertiary mb-1">{hint}</div>}
      <div className="divide-y divide-border-subtle">
        {ideas.map((idea) => {
          const key = `${idea.task_id}:${idea.index}`;
          const p = idea.proposal;
          return (
            <div key={key} className="py-2 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-small text-text-primary font-medium">{p.title}</div>
                <div className="text-caption text-text-tertiary">from "{idea.task_title}"</div>
                <ProposalFacts proposal={p} />
              </div>
              <div className="flex flex-col items-stretch gap-1">
                {!p.parked && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    onClick={() => run(key, () => tasks.parkSpawned(idea.task_id, [idea.index]))}
                  >
                    Later
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy !== null}
                  onClick={() => run(key, () => tasks.rejectSpawned(idea.task_id, [idea.index]))}
                >
                  Drop
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy !== null}
                  onClick={() => run(key, () => tasks.approveSpawned(idea.task_id, [p], [idea.index]))}
                >
                  {p.parked ? "Approve anyway" : "Approve"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
